/**
 * TruncatedCell component.
 *
 * Renders text with CSS truncation and only shows a tooltip when the content
 * is actually clipped. Truncation is detected on pointer-enter by comparing
 * scrollWidth against offsetWidth.
 */

import { useRef, useState } from "react"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"

interface TruncatedCellProps {
  value: string | null | undefined
  className?: string
}

export function TruncatedCell({ value, className }: TruncatedCellProps): React.JSX.Element {
  const ref = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState<boolean>(false)

  if (!value) return <span className="text-muted-foreground">—</span>

  const handlePointerEnter = (e: React.PointerEvent<HTMLSpanElement>): void => {
    e.preventDefault()
    if (ref.current && ref.current.scrollWidth > ref.current.offsetWidth) {
      setOpen(true)
    }
  }

  const handlePointerLeave = (e: React.PointerEvent<HTMLSpanElement>): void => {
    e.preventDefault()
    setOpen(false)
  }

  return (
    <Tooltip open={open}>
      <TooltipTrigger asChild>
        <span
          ref={ref}
          className={`block truncate cursor-default ${className ?? ""}`}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
        >
          {value}
        </span>
      </TooltipTrigger>
      <TooltipContent>{value}</TooltipContent>
    </Tooltip>
  )
}
